import Typography from "@mui/material/Typography";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Select from "@mui/material/Select";

export const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));

export const FilterButtons = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  marginTop: theme.spacing(1),
  "& > .MuiChip-root": {
    [theme.breakpoints.down('xl')]: {
      marginBottom: theme.spacing(0.5),
    },
    marginLeft: theme.spacing(0.5),
  },
  "& > :first-of-type": {
    marginLeft: theme.spacing(2)
  },
}));

export const MobileButton = styled(IconButton)(() => ({
  padding: 0
}));

export const PermissionCardHeader = styled(CardHeader, {
  shouldForwardProp: (prop) => prop !== "grouped"
})<{ grouped?: boolean }>(({ theme, grouped }) => ({
  alignItems: 'center',
  backdropFilter: grouped ? 'blur(4px)' : 'none',
  padding: theme.spacing(1.5),
  position: 'sticky',
  textAlign: 'center',
  top: 0,
  width: '100%',
  zIndex: 1,
  '& .MuiCardHeader-avatar': {
    marginRight: 0,
    textAlign: 'center'
  },
  '& .MuiCardHeader-title': {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: '500',
    justifySelf: 'center',
    textAlign: 'center',
    width: '100%'
  }
}));

export const SinglePermissionActions = styled(CardActions)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBlock: 'auto',
  padding: theme.spacing(1),
  paddingTop: 0,
  width: "100%",
  '& .MuiStack-root': {
    width: '100%'
  }
}));

export const GroupedPermissionActions = styled(CardActions, {
  shouldForwardProp: (prop) => (
    prop !== "isSingleGranteeView" &&
    prop !== "isCreateTemplateView"
  )
})<{
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean
}>(({ theme, isSingleGranteeView, isCreateTemplateView }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBlock: 'auto',
  padding: theme.spacing(1),
  paddingTop: (isSingleGranteeView || isCreateTemplateView) ? 0 : theme.spacing(1),
}));

export const PackedGrid = styled('div')<{ dense?: boolean }>(({ theme, dense }) => ({
  display: 'grid',
  gap: theme.spacing(dense ? 0.5 : 1),
  gridTemplateColumns: `repeat(auto-fill, minmax(${dense ? '150px' : '200px'}, 1fr))`,
  gridAutoFlow: 'dense',
  height: '100%',
  width: '100%'
}));

export const PackedItem = styled(Card, {
  shouldForwardProp: (prop) => (
    prop !== "isSingleGranteeView" &&
    prop !== "isCreateTemplateView" &&
    prop !== "isManageTemplatesView"
  )
})<{
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  cols: number;
  rows: number;
}>(({
  isSingleGranteeView,
  isCreateTemplateView,
  isManageTemplatesView,
  cols,
  rows
}) => ({
  gridColumn: `span ${cols}`,
  gridRow: `span ${rows}`,
  display: 'flex',
  flexDirection: 'column',
  gridTemplateRows: 'min-content minmax(0, 1fr)',
  height: '100%',
  maxHeight: (
    isSingleGranteeView ||
    isCreateTemplateView ||
    isManageTemplatesView
  ) ? 'none' : '130px',
  minHeight: '130px',
  minWidth: (
    cols > 1 && (
      isSingleGranteeView ||
      isCreateTemplateView ||
      isManageTemplatesView
    )) ? '365px' : '150px',
  overflowY: 'auto'
}));

export const GroupedPermissionsContainer = styled(Box)(() => ({
  alignItems: 'center',
  display: 'grid',
  width: '100%'
}));

export const Center = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0
}));

export const NameLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  cursor: 'pointer',
  fontWeight: '500',
  paddingLeft: theme.spacing(1),
  textDecorationColor: theme.palette.text.primary
})) as typeof Link;

export const SmallSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize,
  }
}));

export const PermissionTitleContainer = styled('span')<{ 
  grouped?: boolean 
}>(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  fontSize: '0.9em',
  justifyContent: 'center',
  whiteSpace: 'wrap',
  width: '100%'
}));

export const AlertBarContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: theme.spacing(2),
  width: '100%',
  gap: theme.spacing(1)
}));

export const AlertBarSelectionContainer = styled('span')(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row'
}));

export const NoRowsText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h6.fontSize,
  padding: theme.spacing(4),
  textAlign: 'center',
  width: '100%'
}));
